<template>
  <div class="trigger" :class="{ mobile: isMobile }">
    <h6 v-if="!(isEditingAlert && isMobile)">
      <span v-if="isMobile" @click="backToIndexPage">
        <fa icon="chevron-left" class="icon"></fa>
      </span>
      Filter
    </h6>
    <p>Select trigger filter type (Prefix or Origin AS):</p>
    <span v-if="isMobile" class="separate-line"></span>
    <div class="filter-type">
      <div class="form-check">
        <input
          type="radio"
          class="form-check-input"
          id="prefix-filter"
          :value="FILTER_TYPE.PREFIX"
          v-model="filterType"
        />
        <label for="prefix-filter" class="form-check-label">Prefix</label>
      </div>
      <div
        class="form-check"
        :class="{ disabled: !originASList.length || isOriginASDisabled }"
      >
        <input
          type="radio"
          class="form-check-input"
          :disabled="!originASList.length || isOriginASDisabled"
          id="as-filter"
          :value="FILTER_TYPE.AS"
          v-model="filterType"
        />
        <label for="as-filter" class="form-check-label">
          Origin AS
          <br />
          <span v-if="isOriginASDisabled"
            >Not available for BGP status trigger.</span
          >
        </label>
      </div>
    </div>
    <template v-if="filterType === FILTER_TYPE.PREFIX">
      <div class="prefix-trigger-type">
        <h6>
          Prefix
        </h6>
        <div class="form-check">
          <input
            type="radio"
            class="form-check-input"
            id="prefix-announced-by-asn"
            :value="PREFIX_FILTER_TYPE.PREFIX_ANNOUNCED_BY_ASN"
            v-model="prefixFilterType"
          />
          <label for="prefix-announced-by-asn" class="form-check-label"
            >Any prefix announced by your ASNs.</label
          >
        </div>
        <div class="form-check">
          <input
            type="radio"
            class="form-check-input"
            id="prefix-delegated-to-account"
            :value="PREFIX_FILTER_TYPE.PREFIX_DELEGATED_TO_ACCOUNT"
            v-model="prefixFilterType"
          />
          <label for="prefix-delegated-to-account" class="form-check-label"
            >All prefixes delegated to your account.</label
          >
        </div>
        <div class="form-check">
          <input
            type="radio"
            class="form-check-input"
            id="prefix-selected-individually"
            :value="PREFIX_FILTER_TYPE.PREFIX_SELECTED_INDIVIDUALLY"
            v-model="prefixFilterType"
          />
          <label for="prefix-selected-individually" class="form-check-label"
            >Select individual prefixes.</label
          >
        </div>
        <div
          v-if="prefixFilterType !== PREFIX_FILTER_TYPE.PREFIX_ANNOUNCED_BY_ASN"
          class="match-type-selector"
        >
          <div class="title with-asterisk">Match type</div>
          <div
            v-if="isMatchTypeInvalidInputWarningShown"
            class="warning-description text-danger"
          >
            This field is required.
          </div>
          <Selector
            width="20rem"
            :optionList="matchTypeList"
            :value="matchType"
            @onValueChange="setMatchType"
          ></Selector>
        </div>
        <div
          v-if="
            prefixFilterType === PREFIX_FILTER_TYPE.PREFIX_SELECTED_INDIVIDUALLY
          "
          class="prefix-select-section"
        >
          <div class="title with-asterisk">Prefixes</div>
          <div
            v-if="isPrefixInvalidInputWarningShown"
            class="warning-description text-danger"
          >
            A selection is required.
          </div>
          <div class="prefix-query-wrapper">
            <input
              type="text"
              class="prefix-query form-control"
              placeholder="Type prefix or sub-block"
              v-model.trim="prefixQuery"
            />
            <button
              v-if="isValidSubBlock"
              class="btn btn-primary btn-sm"
              @click="addSubBlock"
            >
              Add
            </button>
          </div>
          <div class="scroll-area">
            <ul v-if="filteredPrefixList.length || isValidSubBlock">
              <li
                v-for="(item, index) in filteredPrefixList"
                :key="item + index"
              >
                <div class="form-group form-check">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    v-model="selectedPrefixList"
                    :value="item"
                    :id="item"
                  />
                  <label class="form-check-label" :for="item">
                    <span>
                      {{ item }}
                    </span>
                  </label>
                </div>
              </li>
            </ul>
            <div v-else class="ml-3 mt-3">
              <i class="text-muted ">No matching prefix or sub-block found</i>
            </div>
          </div>
          <div class="prefix-select-section__footer">
            <button
              type="button"
              class="btn btn-primary"
              @click="toggleSelectAll"
            >
              {{ isSelectedAll ? 'Remove All' : 'Select All' }}
            </button>
          </div>
        </div>
      </div>
    </template>
    <template v-else-if="filterType === FILTER_TYPE.AS">
      <div class="as-trigger-type">
        <h6>
          Origin AS
        </h6>
        <div class="form-check">
          <input
            type="radio"
            class="form-check-input"
            id="as-delegated-to-account"
            :value="AS_FILTER_TYPE.AS_DELEGATED_TO_ACCOUNT"
            v-model="ASFilterType"
          />
          <label for="as-delegated-to-account" class="form-check-label"
            >All origin AS delegated to your account.</label
          >
        </div>
        <div class="form-check">
          <input
            type="radio"
            class="form-check-input"
            id="as-selected-individually"
            :value="AS_FILTER_TYPE.AS_SELECTED_INDIVIDUALLY"
            v-model="ASFilterType"
          />
          <label for="as-selected-individually" class="form-check-label"
            >Select individual Origin ASNs.</label
          >
        </div>
        <div
          v-if="ASFilterType === AS_FILTER_TYPE.AS_SELECTED_INDIVIDUALLY"
          class="as-select-section"
        >
          <div class="title with-asterisk">Origin AS</div>
          <div
            v-if="isASInvalidInputWarningShown"
            class="warning-description text-danger"
          >
            A selection is required.
          </div>
          <div class="as-query-wrapper">
            <input
              type="text"
              class="as-query form-control"
              placeholder="Type origin AS numbers"
              v-model.trim="ASQuery"
            />
          </div>
          <div class="scroll-area">
            <ul v-if="filteredOriginASList.length">
              <li
                v-for="(item, index) in filteredOriginASList"
                :key="item + index"
              >
                <div class="form-group form-check">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    v-model="selectedASList"
                    :value="item"
                    :id="item"
                  />
                  <label class="form-check-label" :for="item">
                    <span>
                      {{ item }}
                    </span>
                  </label>
                </div>
              </li>
            </ul>
            <div v-else class="ml-3 mt-3">
              <i class="text-muted">
                No matching origin AS found
              </i>
            </div>
          </div>
          <div class="as-select-section__footer">
            <button
              type="button"
              class="btn btn-primary"
              @click="toggleSelectAllOriginAS"
            >
              {{ isSelectedAllOriginAS ? 'Remove All' : 'Select All' }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import VueCustomScrollbar from 'vue-custom-scrollbar';
import { mapGetters, mapState } from 'vuex';
import {
  FILTER_TYPE,
  PREFIX_FILTER_TYPE,
  MATCH_TYPE,
  AS_FILTER_TYPE
} from '../../../constants/alertsRoutingStatusConstant';
import Selector from '../../ui/Selector.vue';
import {
  checkIsValidSubBlock,
  sortPrefixByAscending
} from '../../../utils/ipAddressHelpers';

export default {
  components: { VueCustomScrollbar, Selector },
  name: 'FilterRS',
  props: [
    'prevSelectedPrefixList',
    'prefixList',
    'prevFilterType',
    'prevPrefixFilterType',
    'prevMatchType',
    'isInvalidInputWarningShown',
    'prevSelectedASList',
    'prevASFilterType',
    'isOriginASDisabled'
  ],
  data() {
    return {
      prefixQuery: '',
      ASQuery: '',
      isValidSubBlock: false,
      settings: {
        suppressScrollY: true,
        suppressScrollX: true,
        wheelPropagation: true
      },
      selectedPrefixList: [],
      selectedASList: [],
      FILTER_TYPE: FILTER_TYPE,
      PREFIX_FILTER_TYPE: PREFIX_FILTER_TYPE,
      AS_FILTER_TYPE: AS_FILTER_TYPE,
      filterType: this.prevFilterType,
      prefixFilterType: this.prevPrefixFilterType,
      ASFilterType: this.prevASFilterType,
      matchType: this.prevMatchType,
      matchTypeList: [
        {
          name: 'Choose match type...',
          value: 0
        },
        {
          name: 'Exact only',
          value: MATCH_TYPE.EXACT_ONLY
        },
        {
          name: 'More specific only',
          value: MATCH_TYPE.MORE_SPECIFIC_ONLY
        },
        {
          name: 'Exact or more specific',
          value: MATCH_TYPE.EXACT_OR_MORE_SPECIFIC
        }
      ]
    };
  },
  computed: {
    ...mapGetters(['isMobile']),
    ...mapGetters('dataStore', ['asnList']),
    ...mapState('alertsRoutingStatusStore', ['isEditingAlert']),

    filteredPrefixList() {
      if (!this.prefixList || !this.prefixList.length) return [];

      const filteredList = this.prefixList.filter(
        item => item.indexOf(this.prefixQuery) > -1
      );

      this.isValidSubBlock = checkIsValidSubBlock(
        this.prefixQuery,
        this.prefixList
      );

      filteredList.sort((a, b) => sortPrefixByAscending(a, b));

      return filteredList;
    },

    isSelectedAll() {
      return (
        this.filteredPrefixList.length === this.selectedPrefixList.length &&
        this.selectedPrefixList.length > 0
      );
    },

    originASList() {
      return this.asnList.map(item => {
        if (item.id && item.id.trim().indexOf('AS') !== 0) {
          return `AS${item.id}`;
        } else {
          return item.id;
        }
      });
    },

    filteredOriginASList() {
      if (!this.originASList || !this.originASList.length) return [];

      const formattedASNumber = this.ASQuery.replace(/^as|^a/i, '');
      const filteredList = this.originASList.filter(
        item => item.indexOf(formattedASNumber) > -1
      );

      return filteredList;
    },

    isSelectedAllOriginAS() {
      return (
        this.filteredOriginASList.length === this.selectedASList.length &&
        this.selectedASList.length > 0
      );
    },

    isMatchTypeInvalidInputWarningShown() {
      return (
        (this.isInvalidInputWarningShown || this.isEditingAlert) &&
        this.matchType === 0
      );
    },

    isPrefixInvalidInputWarningShown() {
      return (
        (this.isInvalidInputWarningShown || this.isEditingAlert) &&
        this.selectedPrefixList.length === 0
      );
    },

    isASInvalidInputWarningShown() {
      return (
        (this.isInvalidInputWarningShown || this.isEditingAlert) &&
        this.selectedASList.length === 0
      );
    }
  },
  watch: {
    selectedPrefixList: {
      handler(val) {
        this.$emit('onUpdateSelectedPrefixList', val);
      }
    },

    selectedASList: {
      handler(val) {
        this.$emit('onUpdateSelectedASList', val);
      }
    },

    filterType: {
      handler(val) {
        this.$emit('onFilterTypeChange', val);
      }
    },

    prefixFilterType: {
      handler(val) {
        this.$emit('onPrefixFilterTypeChange', val);
      }
    },

    matchType: {
      handler(val) {
        this.$emit('onMatchTypeChange', val);
      }
    },

    ASFilterType: {
      handler(val) {
        this.$emit('onASFilterTypeChange', val);
      }
    }
  },
  mounted() {
    this.selectedPrefixList = this.prevSelectedPrefixList;
    this.selectedASList = this.prevSelectedASList;
  },
  methods: {
    toggleSelectAll() {
      if (!this.isSelectedAll) {
        this.selectedPrefixList = Array.from(
          new Set([...this.filteredPrefixList, ...this.selectedPrefixList])
        );
      } else {
        this.selectedPrefixList = this.selectedPrefixList.filter(item => {
          const isInFilteredPrefixList = this.filteredPrefixList.includes(item);
          return isInFilteredPrefixList ? false : true;
        });
      }
    },

    toggleSelectAllOriginAS() {
      if (!this.isSelectedAllOriginAS) {
        this.selectedASList = Array.from(
          new Set([...this.filteredOriginASList, ...this.selectedASList])
        );
      } else {
        this.selectedASList = this.selectedASList.filter(item => {
          const isInFilteredASList = this.filteredOriginASList.includes(item);
          return isInFilteredASList ? false : true;
        });
      }
    },

    backToIndexPage() {
      this.$emit('backToIndexPage');
    },

    setMatchType(val) {
      this.matchType = val;
    },

    addSubBlock() {
      const isSubBlockExisted = this.prefixList.find(
        prefix => prefix === this.prefixQuery
      );
      if (isSubBlockExisted) return;

      this.selectedPrefixList.unshift(this.prefixQuery);
      this.$emit('onAddSubBlockIntoPrefixList', this.prefixQuery);
      this.prefixQuery = '';
    }
  }
};
</script>

<style lang="scss" scoped>
.trigger {
  position: relative;
  padding: 2rem 1.5rem;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;

  & > p {
    line-height: 1.5rem;
    margin-bottom: 0;
  }
}

h6 {
  font-weight: 600;

  & > span {
    font-size: 1.2rem;
    padding: 0.5rem 1rem;
    margin-left: -1rem;
    vertical-align: bottom;
    color: $text-light-blue;
  }
}

.title {
  margin-bottom: 0.25rem;
}

.separate-line {
  @include separate-line;
}

.prefix-trigger-type,
.as-trigger-type,
.filter-type {
  margin: 0.5rem 0;

  & > div {
    padding: 0.25rem 0 0.25rem 1.5rem;
  }
}

.filter-type {
  display: flex;

  & > div {
    margin-right: 4rem;
  }

  .form-check.disabled {
    input,
    label {
      cursor: not-allowed;
    }

    label > span {
      font-size: 12px;
    }
  }
}

.prefix-trigger-type,
.as-trigger-type {
  padding: 1rem;
  min-height: calc(100% - 130px);
  border: 1px solid #606060;

  & > h6 {
    margin-bottom: 1rem;
  }
}

.match-type-selector,
.prefix-select-section {
  margin-top: 0.5rem;

  .warning-description {
    font-size: 0.75rem;
  }
}

.prefix-select-section {
  .prefix-query-wrapper {
    position: relative;
    width: 66%;
    min-width: 20rem;
    margin-top: 0rem;

    button {
      position: absolute;
      top: 0;
      right: 0;
      max-width: 4rem;
      min-width: 4rem;
      height: 2rem;
      margin-top: 0.2rem;
      margin-right: 0.2rem;
    }
  }
}

.as-select-section {
  .as-query-wrapper {
    position: relative;
    width: 66%;
    min-width: 20rem;
    margin-top: 0rem;
  }

  .warning-description {
    font-size: 0.75rem;
  }
}

p.warning {
  margin-top: 0.5rem;
  margin-bottom: -0.5rem;
}

.prefix-select-section,
.as-select-section {
  height: calc(100% - 12.5rem);

  &__footer {
    margin-top: 0.5rem;
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  @media screen and (min-width: 992px) {
    &__footer {
      span {
        margin-left: 2rem;
      }
    }
  }

  @media screen and (min-width: 1550px) {
    &__footer {
      width: 66%;
      justify-content: space-between;
    }
  }
}

.scroll-area {
  width: 66%;
  min-width: 20rem;
  min-height: 5rem;
  margin-top: 0.25rem;
  overflow-y: auto;
  border: 1px solid $text-grey;

  /deep/ .ps__rail-y {
    background-color: #eee;
    opacity: 0.9;
  }
  /deep/ .ps__thumb-y {
    width: 11px;
  }

  ul {
    padding: 0.5rem 1rem;
    list-style: none;
  }
  li {
    height: 2.5rem;
    min-height: 2.5rem;
    line-height: 2.5rem;

    & > div {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0;

      input {
        margin-top: -0.1rem;
        height: 1.2rem;
        width: 1.2rem;
      }
      label {
        padding-left: 0.5rem;
      }
    }
  }
}

.mobile {
  padding: 1rem;

  h6 {
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 1rem;
  }

  p.warning {
    margin-top: -0.5rem;
    margin-bottom: 0.5rem;
  }

  .filter-type {
    flex-direction: column;
    margin-top: -0.5rem;
  }

  .prefix-trigger-type,
  .as-trigger-type {
    margin: 0;
    margin-top: 1rem;

    .match-type-selector,
    .prefix-select-section,
    .as-select-section {
      padding-left: 0.25rem;
    }

    .prefix-query-wrapper,
    .scroll-area,
    .as-query-wrapper {
      min-width: 100%;
      max-width: 100%;
    }
  }

  .scroll-area {
    min-height: 5rem;
  }

  .prefix-select-section {
    &__footer {
      margin-top: 0.5rem;
      display: flex;
      flex-direction: column-reverse;
      align-items: flex-start;

      span {
        margin-bottom: 0.5rem;
        font-size: 0.8rem;
      }
    }
  }
}
</style>
